<template>
    <!-- COMMENTS -->
    <div>
        <l-surveys-insights-filters
            ref="filters"
            :survey="survey"
            :allowed-filters="[
                'period',
                'question',
                'sentiment',
                'replied',
                'classify',
                'onlyShowUnseen',
                'onlyShowAssigned',
            ]"
            :classify-options="classifyOptions"
            @change="onFilterChange"
        ></l-surveys-insights-filters>

        <hr />

        <l-bubble-graph
            v-if="renderBubbleGraph"
            v-show="showBubbleGraph"
            :topics="topics"
            :width="width"
            class="tw-mb-4"
            @click="onBubbleClick"
            @hide="showBubbleGraph = false"
            @show="showBubbleGraph = true"
        ></l-bubble-graph>

        <l-infinite-scroll
            :list="list"
            :on-list-bottom="fetchMore"
            :attach-to-window-scroll="true"
            :no-data-text="$t('surveys.details.labelNoComments')"
            :can-load-more="!!hasMore"
        >
            <template #item="{ item: comment }">
                <div
                    class="tw-flex tw-mt-8"
                    :class="{
                        'tw-border-0 tw-border-t tw-border-solid tw-border-gray-100 tw-pt-8':
                            renderBubbleGraph && showBubbleGraph,
                    }"
                >
                    <div
                        class="tw-mr-8 tw-flex-shrink-0 tw-flex tw-flex-col tw-justify-start tw-items-center"
                    >
                        <l-profile-picture
                            v-if="comment.senderId"
                            :user="{
                                fullname: comment.senderName,
                                picture: comment.senderPicture,
                                _id: comment.senderId,
                            }"
                            size="xm"
                            class="tw-mb-4"
                            img-classes="tw-ring-2 tw-ring-white"
                        />

                        <div class="tw-flex tw-justify-center tw-items-center tw-space-x-4">
                            <span
                                class="label label-light tw-tabular-nums"
                                :class="{
                                    'label-success':
                                        comment.scaleType === 'nps' || !comment.scaleType
                                            ? comment.amount > 8
                                            : comment.amount > 3,
                                    'label-warning':
                                        comment.scaleType === 'nps' || !comment.scaleType
                                            ? comment.amount <= 6
                                            : comment.amount < 2.5,
                                }"
                            >
                                {{ comment.amount || "-" }}
                            </span>

                            <span
                                v-if="!comment.sentimentScore || comment.sentimentScore == 0"
                                v-b-popover.hover.top="$t('surveys.details.tooltipNeutralComment')"
                                class="label sentiment color-grey-light icon"
                            />
                            <i
                                v-else
                                v-b-popover.hover.top="
                                    comment.sentimentScore > 0
                                        ? $t('surveys.details.tooltipPositiveComment')
                                        : $t('surveys.details.tooltipNegativeComment')
                                "
                                class="fal color-grey-light icon"
                                :class="{
                                    'fa-thumbs-up': comment.sentimentScore > 0,
                                    'fa-thumbs-down': comment.sentimentScore <= 0,
                                }"
                            />
                        </div>
                    </div>
                    <div class="tw-flex-1">
                        <div class="tw-mb-4">
                            <h5 class="tw-m-0">
                                <div class="tw-font-bold tw-line-clamp-1" :title="comment.name">
                                    {{ comment.name }}
                                </div>
                            </h5>
                            <div class="tw-flex tw-text-gray-400 tw-space-x-2">
                                <div class="tw-flex-shrink-0">
                                    {{ comment.senderName }}
                                    <span v-show="comment.isAuthor">
                                        {{ $t("surveys.details.you") }}
                                    </span>
                                </div>
                                <span>&#124;</span>
                                <span class="tw-flex-shrink-0">{{
                                    comment.date | moment("MMM D, YYYY")
                                }}</span>
                            </div>
                        </div>

                        <div class="tw-m-0 tw-p-4 tw-rounded-md tw-bg-gray-50 tw-relative">
                            <l-text :text="comment.content"></l-text>
                        </div>

                        <div class="tw-mt-4 tw-flex tw-justify-start tw-items-center tw-space-x-4">
                            <l-base-button
                                v-if="comment.canUserCommentOnSignal"
                                :button-types="['inline', 'size-sm', 'primary', 'raised']"
                                class="btn btn-primary"
                                type="button"
                                data-test-id="form-continue"
                                @click.native="showDetail(comment._id)"
                            >
                                <template v-if="!comment.hasComments">
                                    {{ $t("surveys.details.anonymousConversation.start") }}
                                </template>
                                <template v-else>
                                    {{ $t("surveys.details.anonymousConversation.view") }}
                                </template>
                            </l-base-button>

                            <l-base-button
                                v-b-popover.hover.top="
                                    comment.isMarkedAsSeen
                                        ? $t('surveys.details.btnMarkAsUnseenHint')
                                        : $t('surveys.details.btnMarkAsSeenHint')
                                "
                                :button-types="['inline', 'size-sm', 'default', 'raised']"
                                type="button"
                                @click.native="markAsResolved(comment)"
                            >
                                <template v-if="comment.isMarkedAsSeen">
                                    <i class="fal fa-check tw-text-green-500" />
                                    <span>{{ $t("surveys.details.btnMarkAsUnseen") }}</span>
                                </template>
                                <template v-else>
                                    <i class="fal fa-eye" />
                                    <span>{{ $t("surveys.details.btnMarkAsSeen") }}</span>
                                </template>
                            </l-base-button>
                        </div>

                        <div
                            v-if="hasApiErrorForCommentId === comment._id"
                            class="tw-mt-2 tw-text-sm tw-text-red-700"
                        >
                            {{ $t("surveys.details.saveError") }}
                        </div>
                    </div>
                </div>
            </template>
        </l-infinite-scroll>

        <br />

        <!-- modal for conversation -->
        <l-modal v-if="showModal" size="m" :close-on-backdrop-click="false" @close="goBack">
            <template #header>{{ $t("surveys.modal.title") }} </template>
            <template #body>
                <div id="modal-body" class="modal-body">
                    <div class="tw-flex">
                        <div
                            class="tw-mr-8 tw-flex-shrink-0 tw-flex tw-flex-col tw-justify-start tw-items-center"
                        >
                            <l-profile-picture
                                v-if="detail.senderId"
                                :user="{
                                    fullname: detail.senderName,
                                    picture: detail.senderPicture,
                                    _id: detail.senderId,
                                }"
                                size="xm"
                                class="tw-mb-4"
                                img-classes="tw-ring-2 tw-ring-white"
                            />

                            <div class="tw-flex tw-justify-center tw-items-center tw-space-x-4">
                                <span
                                    v-if="detail.amount"
                                    class="label label-light tw-tabular-nums"
                                    :class="{
                                        'label-success':
                                            detail.scaleType === 'nps' || !detail.scaleType
                                                ? detail.amount > 8
                                                : detail.amount > 3,
                                        'label-warning':
                                            detail.scaleType === 'nps' || !detail.scaleType
                                                ? detail.amount <= 6
                                                : detail.amount <= 2.5,
                                    }"
                                >
                                    {{ detail.amount }}/{{
                                        detail.scaleType === "nps" || !detail.scaleType ? 10 : 5
                                    }}
                                </span>
                                <span v-else class="label label-light tw-tabular-nums">
                                    -
                                </span>
                            </div>
                        </div>
                        <div class="tw-flex-1">
                            <div class="tw-mb-4">
                                <h5 class="tw-m-0">
                                    <div class="tw-font-bold tw-line-clamp-1" :title="detail.name">
                                        {{ detail.name }}
                                    </div>
                                </h5>
                                <div class="tw-flex tw-text-gray-400 tw-space-x-2">
                                    <div class="tw-flex-shrink-0">
                                        {{ detail.senderName }}
                                        <span v-show="detail.isAuthor">
                                            {{ $t("surveys.details.you") }}
                                        </span>
                                    </div>
                                    <span>&#124;</span>
                                    <span class="tw-flex-shrink-0">{{
                                        detail.date | moment("MMM D, YYYY")
                                    }}</span>
                                </div>
                            </div>

                            <div class="tw-m-0 tw-p-4 tw-rounded-md tw-bg-gray-50 tw-relative">
                                <l-text :text="detail.content"></l-text>
                            </div>
                            <div>
                                <div style="height: 15px;"></div>
                                <span class="color-primary">
                                    <span
                                        v-for="reaction in reactions"
                                        :key="reaction.key"
                                        class="tw-mr-4"
                                        @click="
                                            detail.canUserCommentOnSignal &&
                                                fireReaction({
                                                    ...detail,
                                                    type: reaction.type,
                                                    isCreate: !detail.myReactions.includes(
                                                        reaction.type,
                                                    ),
                                                })
                                        "
                                        @keyup.enter="
                                            detail.canUserCommentOnSignal &&
                                                fireReaction({
                                                    ...detail,
                                                    type: reaction.type,
                                                    isCreate: !detail.myReactions.includes(
                                                        reaction.type,
                                                    ),
                                                })
                                        "
                                    >
                                        <i
                                            v-b-popover.hover.top="
                                                $t(`surveys.details.reaction.${reaction.type}`)
                                            "
                                            class="clickable"
                                            :class="{
                                                [reaction.icon]: true,
                                                fal: !detail.myReactions.includes(reaction.type),
                                                fa: detail.myReactions.includes(reaction.type),
                                            }"
                                        ></i>
                                        <span
                                            v-b-popover.hover.top="
                                                reactionUsers[reaction.type].join(',')
                                            "
                                            class="tw-ml-1"
                                            >{{
                                                existingReactions[reaction.type].length
                                                    ? existingReactions[reaction.type].length
                                                    : 0
                                            }}</span
                                        >
                                    </span>
                                </span>
                                <span class="pull-right tw-text-gray-400">
                                    {{ $t(`surveys.details.reaction.userReaction`) }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div v-if="detail.canUserCommentOnSignal">
                        <div
                            v-for="comment in detail.conversations"
                            :key="comment._id"
                            class="tw-mt-8 tw-pt-8 tw-border-0 tw-border-t tw-border-solid tw-border-gray-100"
                        >
                            <div class="tw-flex tw-items-center tw-space-x-4 tw-justify-start">
                                <div class="tw-flex-shrink-0">
                                    <l-profile-picture
                                        :user="{
                                            fullname: comment.senderName,
                                            picture: comment.senderPicture,
                                            _id: comment.senderId,
                                        }"
                                        size="xm"
                                        img-classes="tw-ring-2 tw-ring-white"
                                    />
                                </div>

                                <div class="tw-flex-shrink-0 tw-font-semibold tw-leading-none">
                                    {{ comment.senderName }}
                                    <span v-if="comment.isAuthor">
                                        {{ $t("surveys.details.you") }}
                                    </span>
                                    <span v-else-if="comment.isSignalAuthor">
                                        ({{ $t("surveys.details.author") }})
                                    </span>

                                    <div class="tw-font-normal tw-leading-normal tw-text-gray-400">
                                        {{ comment.date | moment("MMM D, YYYY") }}
                                    </div>
                                </div>
                            </div>

                            <div class="tw-flex tw-mt-4 tw-justify-start">
                                <div
                                    class="tw-p-4 tw-rounded-md tw-relative tw-flex-1"
                                    :class="{
                                        'tw-bg-gray-50': !comment.isManagerNote,
                                        'tw-bg-yellow-100 tw-border tw-border-solid tw-border-yellow-200':
                                            comment.isManagerNote,
                                    }"
                                >
                                    <l-text :text="comment.content"></l-text>

                                    <div
                                        v-if="comment.isManagerNote"
                                        class="tw-absolute tw--bottom-5 tw-right-2 tw-text-xs"
                                    >
                                        {{ $t("surveys.modal.internalNote") }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            class="tw-mt-8 tw-border-0 tw-border-t-2 tw-border-solid tw-border-gray-100 tw-pt-4"
                        >
                            <l-tab-navigation
                                v-if="detail.isManagingConversation"
                                :tabs="modalActionTabs"
                                :selected.sync="selectedModalActionTab"
                            />

                            <template v-if="selectedModalActionTab.id === 'reply'">
                                <l-input-text
                                    v-model="commentForm.newCommentContent"
                                    :label="
                                        `${$t('surveys.modal.input.commentLabel')}${
                                            !detail.senderId && detail.isAuthor
                                                ? ` (${$t(
                                                      'surveys.modal.input.commentLabelExtension',
                                                  )}):`
                                                : ` (${$t(
                                                      'surveys.modal.input.commentLabelExtensionParticipant',
                                                  )}):`
                                        }`
                                    "
                                    placeholder=""
                                    type="textarea"
                                    class="tw"
                                    :input-class="
                                        `${commentForm.isManagerNote ? '!tw-bg-yellow-100' : ''}`
                                    "
                                />

                                <label
                                    class="tw-flex tw-items-center tw-justify-end tw-text-gray-600 tw-mt-2 tw-cursor-help tw-select-none"
                                >
                                    <i
                                        v-b-popover.hover.top="
                                            !detail.senderId && detail.isAuthor
                                                ? $t('surveys.modal.input.publicReplyHintAnon')
                                                : $t('surveys.modal.input.publicReplyHint')
                                        "
                                        class="fal fa-question-circle tw-ml-2 tw-text-gray-400"
                                        :class="
                                            !detail.senderId && detail.isAuthor
                                                ? 'fa fa-shield-check'
                                                : 'fal fa-question-circle'
                                        "
                                    />
                                </label>
                            </template>

                            <div
                                v-else-if="
                                    detail.isManagingConversation &&
                                        selectedModalActionTab.id === 'moderate'
                                "
                            >
                                <l-input-text
                                    v-model="commentForm.newCommentContent"
                                    :label="`${$t('surveys.modal.input.internalNoteLabel')}:`"
                                    placeholder=""
                                    type="textarea"
                                    class="tw"
                                    input-class="!tw-bg-yellow-100"
                                    :optional="true"
                                />

                                <label
                                    class="tw-flex tw-items-center tw-justify-end tw-text-gray-600 tw-mt-2 tw-cursor-help tw-select-none"
                                >
                                    <input
                                        v-model="commentForm.isManagerNote"
                                        disabled
                                        class="tw-cursor-help tw-hidden"
                                        type="checkbox"
                                    />

                                    <i
                                        v-b-popover.hover.top="
                                            $t('surveys.modal.input.internalNoteHint')
                                        "
                                        class="fal fa-question-circle tw-ml-2 tw-text-gray-400"
                                    />
                                </label>

                                <div
                                    class="tw-mt-4 tw-pt-9 tw-border-0 tw-border-t-2 tw-border-solid tw-border-gray-100"
                                >
                                    <l-input-typeahead
                                        class="tw"
                                        :show-picture="true"
                                        :show-checkbox="false"
                                        :label="
                                            `${$t('surveys.modal.input.assignConversationToUser')}:`
                                        "
                                        :placeholder="$t('userlist.show.placeholderType')"
                                        :data="usersTypeaheadData"
                                        :on-select="onUserAssign"
                                        :on-input-change="typeAheadOnInputChange"
                                        :max-matches="200"
                                    />
                                </div>

                                <div class="tw-mt-1 tw-space-x-2">
                                    <span
                                        v-for="(assignedUser, index) in commentForm.assignedUsers"
                                        :key="assignedUser._id"
                                        class="label label-default tw-select-none"
                                    >
                                        {{ assignedUser.fullname }}
                                        <i
                                            v-if="assignedUser._id !== user._id"
                                            tabindex="0"
                                            class="fa fa-remove tw-ml-1 clickable"
                                            @click="onRemoveAssignedUser(index)"
                                            @keyup.enter="onRemoveAssignedUser(index)"
                                        ></i>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="tw-flex tw-items-center tw-justify-end tw-mt-8 tw-space-x-4">
                            <l-base-button
                                :button-types="['inline', 'default']"
                                type="button"
                                @click.native="goBack"
                            >
                                {{ $t("surveys.modal.button.cancel") }}
                            </l-base-button>

                            <l-base-button
                                :button-types="['inline', 'primary', 'raised']"
                                type="button"
                                :disabled="
                                    (!commentForm.newCommentContent && !hasModifiedAssignedUsers) ||
                                        isLoading
                                "
                                @click.native="createComment"
                            >
                                <template v-if="hasModifiedAssignedUsers">
                                    {{ $t("surveys.modal.button.save") }}
                                </template>
                                <template v-else>
                                    {{ $t("surveys.modal.button.send") }}
                                </template>
                            </l-base-button>
                        </div>

                        <div
                            v-if="showSaveSuccessMessage"
                            class="tw-text-right tw-text-xs tw-text-gray-400 tw-mt-1"
                        >
                            <i class="fal fa-check tw-mr-1" />
                            {{ $t("surveys.modal.button.saved") }}
                        </div>
                    </div>
                </div>
            </template>
        </l-modal>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import format from "@/utils/format-typeahead";

import LText from "@/components/LText";
import LModal from "@/components/LModal";

import icon from "@/img/icons/surveys.png";
import LInfiniteScroll from "@/components/LInfiniteScroll";
import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";

import LSurveysInsightsFilters from "@/components/LSurveysInsightsFilters.vue";
import LProfilePicture from "@/components/LProfilePicture.vue";
import LBubbleGraph from "@/components/LBubbleGraph";
import LBaseButton from "@/components/LBaseButton";
import LTabNavigation from "@/components/LTabNavigation";

export default {
    components: {
        LSurveysInsightsFilters,
        LInfiniteScroll,
        LText,
        LModal,
        LProfilePicture,
        LInputText,
        LInputTypeahead,
        LBubbleGraph,
        LBaseButton,
        LTabNavigation,
    },
    props: {
        survey: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            error: false,

            page: 0,
            svgLoading: true,

            filters: {},

            showModal: false,
            filterOptions: {},

            commentForm: {
                newCommentContent: "",
                isManagerNote: false,
                assignedUsers: [],
            },

            hasModifiedAssignedUsers: false,

            width: 0,
            renderBubbleGraph: false,
            showBubbleGraph: true,
            classifyOptions: [],

            hasApiErrorForCommentId: null,

            isLoading: false,
            showSaveSuccessMessage: false,

            selectedModalActionTab: { id: "reply" },

            isReactionFired: {},
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            user: "users/current/user",

            list: "surveys/comments/list",
            hasMore: "surveys/comments/hasMore",
            topics: "surveys/comments/topics",
            getDetail: "surveys/comments/detail",
            minObsAnonymity: "surveys/comments/minObsAnonymity",
            usersSearchList: "users/search/list",
        }),
        usersTypeaheadData() {
            const assignedUserIds = this.commentForm.assignedUsers?.map(user => user._id) ?? [];
            const relevantUsers = this.usersSearchList
                // for non anonymous surveys we don't want the original author
                // to be selectable
                .filter(user => user._id !== this.detail?.senderId)
                .filter(user => user._id !== this.user?._id)
                .filter(user => !assignedUserIds.includes(user._id));

            return relevantUsers.map(format.users);
        },
        detail() {
            const commentId = this.$route.params.category;
            return this.getDetail(commentId);
        },

        accountName() {
            return this.user.teamSettings.name;
        },
        reactionUsers() {
            const defaultV = { like: [], thanks: [], agree: [], talk: [] };

            for (const [key, users] of Object.entries(this.detail?.reaction)) {
                users?.forEach(user => {
                    if (!defaultV[key]) {
                        defaultV[key] = [];
                    }
                    if (user._id == this.user?._id) {
                        defaultV[key].push("You");
                    } else {
                        defaultV[key].push(user.fullname);
                    }
                });
            }
            return defaultV;
        },
        existingReactions() {
            const defaultV = { like: 0, thanks: 0, agree: 0, talk: 0 };
            return { ...defaultV, ...this.detail?.reaction };
        },
        reactionNumbers() {
            let n = 0;
            for (const reaction of Object.values(this.detail?.reaction)) {
                n += reaction.length;
            }
            return n;
        },

        modalActionTabs() {
            return [
                {
                    id: "reply",
                    label: this.$t("surveys.modal.tabs.publicReply"),
                    visible: true,
                },
                {
                    id: "moderate",
                    label: `${this.$t("surveys.modal.tabs.notesAndAssign")} (${
                        this.commentForm.assignedUsers.length
                    })`,
                },
            ];
        },
        reactions() {
            return [
                {
                    name: this.$t("surveys.details.reaction.like"),
                    type: "like",
                    icon: "fa-lightbulb-on",
                },
                {
                    name: this.$t("surveys.details.reaction.thanks"),
                    type: "thanks",
                    icon: "fa-smile",
                },
                {
                    name: this.$t("surveys.details.reaction.agree"),
                    type: "agree",
                    icon: "fa-thumbs-up",
                },
                {
                    name: this.$t("surveys.details.reaction.talk"),
                    type: "talk",
                    icon: "fa-comment-plus",
                },
            ];
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler(route) {
                if (route.params.category && route.params.category != "") {
                    this.showModal = true;
                } else {
                    this.showModal = false;
                }
            },
        },
        minObsAnonymity: {
            immediate: true,
            handler(minObsAnonymity) {
                this.$emit("change", minObsAnonymity);
            },
        },
        topics: {
            immediate: true,
            handler(topics) {
                this.classifyOptions = topics.map(d => {
                    let cateId = "";
                    if (d._id && d._id.skillcategory) {
                        cateId = d._id.skillcategory;
                    }
                    return { _id: `${cateId}|${d.category}`, name: d.category };
                });
            },
        },
        "detail.assignedUsers": {
            immediate: true,
            handler(assignedUsers = []) {
                // update local form default once API request finished in router
                this.commentForm.assignedUsers = [...assignedUsers];
            },
        },
        selectedModalActionTab(newValue) {
            this.commentForm.isManagerNote = newValue?.id === "moderate";
        },
    },

    created() {
        this.icon = icon;
        this.classifyOptions = this.topics.map(d => {
            let cateId = "";
            if (d._id && d._id.skillcategory) {
                cateId = d._id.skillcategory;
            }
            return { _id: `${cateId}|${d.category}`, name: d.category };
        });
    },
    mounted() {
        this.width = document.querySelector(".card-block").clientWidth - 50;
    },
    methods: {
        async fireReaction({ surveyId, _id: signalId, type, isCreate } = {}) {
            await this.$store
                .dispatch("surveys/comments/reaction", {
                    payload: { isCreate, surveyId, signalId, type, currentUser: this.user },
                })
                .then(() => {
                    if (isCreate) {
                        // when creating
                        this.$set(this.isReactionFired, signalId, true);
                        setTimeout(() => {
                            this.isReactionFired[signalId] = false;
                        }, 5000);
                    }
                });
        },
        async markAsResolved({ surveyId, _id: signalId, isMarkedAsSeen } = {}) {
            this.hasApiErrorForCommentId = null;

            try {
                await this.$store.dispatch("surveys/comments/markAsSeen", {
                    payload: { isMarkedAsSeen: !isMarkedAsSeen, surveyId, signalId },
                });
            } catch (err) {
                this.hasApiErrorForCommentId = signalId;
            }
        },
        onBubbleClick(d) {
            const option = this.$refs.filters.classifyOptions.find(o => {
                let cateId = "";
                if (d._id && d._id.skillcategory) {
                    cateId = d._id.skillcategory;
                }
                return o._id === `${cateId}|${d.category}`;
            });
            option.selected = this.$refs.filters?.filters["classify"]?.some(
                m => m._id == option._id,
            );
            this.$refs.filters.selectOption("classify", option);
        },
        onFilterChange(type, { filters }) {
            // everytime we change the filter, we need to reset the page number

            this.filters = filters;
            this.page = 0;
            this.fetchMore(true); // and then simply call fetchMore lah
        },
        fetchMore(force) {
            if (!force && !this.hasMore) {
                // when it's force(in the case filter change, we ignore this field)
                return;
            }
            this.$store
                .dispatch("surveys/comments/get", {
                    payload: {
                        survey: this.$route.params.id,
                        format: "json",
                        filterBy: this.filters,
                        page: this.page,
                        pageSize: 50,
                    },
                })
                .then(() => {
                    this.page += 1;
                    this.renderBubbleGraph = true;
                });
        },
        showDetail(commentId) {
            this.$router.push(`/surveys/insights/${this.$route.params.id}/comments/${commentId}`);
        },
        async createComment() {
            this.isLoading = true;

            try {
                await this.$store.dispatch("surveys/comments/create", {
                    payload: {
                        survey: this.$route.params.id,
                        surveySignal: this.detail._id,
                        content: this.commentForm.newCommentContent,
                        isManagerNote: this.commentForm.isManagerNote,
                        ...(!this.detail?.isAuthor
                            ? { assignedUsers: this.commentForm.assignedUsers }
                            : {}),
                    },
                });

                // reset form
                this.commentForm.newCommentContent = "";
                this.hasModifiedAssignedUsers = false;
                this.showSaveSuccessMessage = true;

                setTimeout(() => {
                    this.showSaveSuccessMessage = false;
                }, 3000);
            } catch {
                // API is unresponsive, no action
            } finally {
                this.isLoading = false;
            }
        },

        export() {
            this.$store.dispatch("surveys/comments/export", {
                payload: {
                    survey: this.$route.params.id,
                    filterBy: this.filters,
                    format: "xlsx",
                },
            });
        },
        goTo(url) {
            this.$router.push(url);
        },
        goBack() {
            this.$router.push(`/surveys/insights/${this.$route.params.id}/comments`);
        },
        typeAheadOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },
        onUserAssign(user = {}) {
            this.commentForm.assignedUsers.push({ _id: user._id, fullname: user.fullname });
            this.hasModifiedAssignedUsers = true;
        },
        onRemoveAssignedUser(index) {
            this.commentForm.assignedUsers.splice(index, 1);
            this.hasModifiedAssignedUsers = true;
        },
    },
};
</script>

<style scoped>
.sentiment {
    margin-right: 8px;
    height: 12px;
    width: 12px;
    padding: 0;
    border-radius: 100%;
    display: block !important;
}
.icon {
    margin-left: 11px;
}
.modal-body {
    padding: 0;
}
.modal-footer {
    padding: 0;
}
</style>
